import { useContext, useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { ContentBody, ContentHeader } from 'components/shared/content'
import {
    CheckboxTableRow,
    ExTable,
    FixedFooter,
    ModalButton,
    OrderTableCell,
    TableBody,
    TableCell,
    TableHead,
    TableWrapper,
    useMultipleCheckForm,
} from 'components/shared/widget'
import { Institution } from 'entities/institution'
import { GlobalStateContext } from 'contexts/global_state_context'
import { Flex } from 'components/shared/flex'
import { Badge, Button, ButtonGroup, Table, ToggleButton } from 'react-bootstrap'
import { useCompleteSendHistoryApi, usePostSendHistoryApi } from 'api/send_history'
import { SendHistory, SendHistoryForm } from 'entities/send_history'
import { useEffectSkipFirst, useForm } from 'utils/hooks'
import { Pagination } from 'components/shared/pagination'
import { CheckboxField, InputField } from 'components/shared/input'
import _ from 'lodash'
import { useAutoResultPdfUploadApi, useFetchSamplesApi, AutoResultPdfUploadForm, usePutSampleResultStatusApi, usePutSampleResultConfirmApi } from 'api/sample'
import { Sample, SampleForm, SampleSearchForm } from 'entities/sample'
import { ResultV2ViewModel } from 'view_models/result_v2/result_v2_view_model'
import { downloadFile, toDateText } from 'utils/util'
import qs from 'qs'
import { SampleViewModel } from 'view_models/sample_view_model'

type Props = {
    history: H.History
}

const INITIAL_INSPECTION_METHOD = 'NGS'

const SendHistoriesPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const isVender = !!process.env.REACT_APP_IS_VENDOR
    const searchForm = useForm<SampleSearchForm>({
        sampleStatus: 'COMPLETED,SENT',
        send_date_isnull: true,
        ordering: 'sample_deadline_date,sample_institution_id',
        withPureResults: true,
        inspectionMethod: INITIAL_INSPECTION_METHOD,
        page: 1,
        perPage: 50,
        institutionIsSpecialResult: isVender ? false : undefined,
        request_form__is_result_confirm_required: false,
    })
    const api = useFetchSamplesApi(searchForm)
    const multipleCheckForm = useMultipleCheckForm<Sample>()
    const sendHistoryForm = useForm<SendHistoryForm>({})
    const postSendHistoryApi = usePostSendHistoryApi()
    const samplesSearchForm = useForm<SampleSearchForm>({
        sampleStatus: 'COMPLETED,SENT',
        send_date_isnull: true,
        withPureResults: true,
        inspectionMethod: INITIAL_INSPECTION_METHOD,
        request_form__is_result_confirm_required: false,
    })
    const confirmSampleSearchForm = useForm<SampleSearchForm>({
        sampleStatus: 'COMPLETED,SENT',
        send_date_isnull: true,
        withPureResults: true,
        inspectionMethod: INITIAL_INSPECTION_METHOD,
        request_form__is_result_confirm_required: true,
    })
    const samplesApi = useFetchSamplesApi(samplesSearchForm)
    const confirmSampleApi = useFetchSamplesApi(confirmSampleSearchForm)

    const updateForm = useForm<SendHistoryForm>({ status: 'POSTED' })
    const isHomeTest = !!process.env.REACT_APP_IS_HOME_TEST
    const completeApi = useCompleteSendHistoryApi()
    const [showCompleteModal, setShowCompleteModal] = useState<boolean>(false)
    const [selectedSendHistory, setSelectedSendHistory] = useState<SendHistory>({})
    const form = useForm<SampleForm[]>([])
    const sampleResultStatusApi = usePutSampleResultStatusApi()
    const sampleResultConfirmApi = usePutSampleResultConfirmApi()

    const AutoResultPdfUploadForm = useForm<AutoResultPdfUploadForm>({ sampleIds: [] })
    const autoResultPdfUploadApi = useAutoResultPdfUploadApi()

    const [currentState, setCurrentState] = useState<'completed' | 'sent'>('completed')
    const [inspectionMethod, setInspectionMethod] = useState<'NGS' | 'PCR'>(INITIAL_INSPECTION_METHOD)
    const [isConfirmMode, setIsConfirmMode] = useState<boolean>(false)
    const [samples, setSamples] = useState<Sample[]>([])

    const fetchSamples = () => {
        api.execute()
        confirmSampleApi.execute()
        samplesApi.execute()
    }

    useEffect(() => {
        fetchSamples()
    }, [])

    useEffectSkipFirst(() => {
        if ((api.isSuccess() && !isConfirmMode) || (confirmSampleApi.isSuccess() && isConfirmMode)) {
            multipleCheckForm.reset()

            const samples = isConfirmMode ? confirmSampleApi.response.results : api.response.results

            form.set(
                samples.map((sample) => {
                    const s = { ...sample }
                    delete s.patient
                    delete s.institution
                    delete s.productItems
                    delete s.results
                    delete s.resultPdf
                    return s
                })
            )

            setSamples(samples)
        }
    }, [api.loading, confirmSampleApi.loading, isConfirmMode])

    useEffectSkipFirst(() => {
        globalState.setLoading(sampleResultStatusApi.loading)
        if (sampleResultStatusApi.isSuccess()) {
            fetchSamples()
        }
    }, [sampleResultStatusApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(completeApi.loading)
        if (completeApi.isSuccess()) {
            fetchSamples()
        }
    }, [completeApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(sampleResultConfirmApi.loading)
        if (sampleResultConfirmApi.isSuccess()) {
            fetchSamples()
        }
    }, [sampleResultConfirmApi.loading])

    useEffect(() => {
        const queryParams = qs.stringify({ ...searchForm.object, page: api.pageSet.page, perPage: api.pageSet.perPage })
        if (props.history.location.search.replace('?', '') !== queryParams) {
            props.history.replace(`send_histories?${queryParams}`)
        }
    }, [searchForm.object])

    useEffectSkipFirst(() => {
        sendHistoryForm.updateObject(
            'institutionIds',
            multipleCheckForm.form.object.items.map((i) => i.id)
        )
        AutoResultPdfUploadForm.updateObject(
            'sampleIds',
            multipleCheckForm.form.object.items.map((i) => i.id!)
        )
    }, [multipleCheckForm.form.object.items])

    const getSendUnitInstitutionList = (): Institution[] => {
        // sampleからユニークな病院を抽出するが、1病院あたり5検体を超える場合、5検体区切りで、同一病院の重複を許可する（5検体分がレターパックで送れる最大値のため）
        const uniqInstitutions = _.uniqBy(
            selectedSendHistory.samples?.map((s) => s.institution),
            'id'
        )
        const institutions: Institution[][] = uniqInstitutions.map((institution) => {
            if (!institution) {
                return []
            }
            const chunkCount = Math.ceil((selectedSendHistory.samples?.filter((s) => s.institution?.id === institution?.id).length ?? 0) / 5)
            return new Array(chunkCount).fill(institution)
        })
        return institutions.flatMap((l) => l)
    }

    useEffectSkipFirst(() => {
        if (isHomeTest) {
            updateForm.update((f) => {
                f.sampleTrackNumbers = selectedSendHistory.samples?.map((s) => {
                    return { sampleId: s.id, sampleCode: s.code }
                })
            })
        } else {
            updateForm.update((f) => {
                f.institutionTrackNumbers = getSendUnitInstitutionList().map((i) => {
                    return { institutionName: i.name, institutionId: i.id }
                })
            })
        }
    }, [selectedSendHistory])

    useEffectSkipFirst(() => {
        if (currentState === 'completed') {
            searchForm.update((f) => {
                f.sampleStatus = 'COMPLETED,SENT'
                f.send_date_isnull = true
                f.ordering = 'sample_deadline_date,sample_institution_id'
            })
        } else {
            searchForm.update((f) => {
                f.sampleStatus = 'SENT'
                f.send_date_isnull = false
                f.ordering = '-sample_send_date'
            })
        }
    }, [currentState])

    useEffectSkipFirst(() => {
        searchForm.update((f) => {
            f.inspectionMethod = inspectionMethod
        })
        samplesSearchForm.update((f) => {
            f.inspectionMethod = inspectionMethod
        })
        confirmSampleSearchForm.update((f) => {
            f.inspectionMethod = inspectionMethod
        })
    }, [inspectionMethod])

    useEffectSkipFirst(() => {
        globalState.setLoading(autoResultPdfUploadApi.loading)
        if (autoResultPdfUploadApi.isSuccess()) {
            api.execute()
        }
    }, [autoResultPdfUploadApi.loading])

    return (
        <>
            <ContentHeader
                headerTitle="結果表印刷"
                left={
                    <>
                        <ButtonGroup>
                            <ToggleButton
                                id={`radio-completed`}
                                disabled={api.loading}
                                type="radio"
                                variant={'outline-primary'}
                                name="radio"
                                value={'completed'}
                                checked={currentState === 'completed'}
                                onChange={(e) => setCurrentState('completed')}
                            >
                                未発送
                            </ToggleButton>
                            <ToggleButton
                                id={`radio-sent`}
                                disabled={api.loading}
                                type="radio"
                                variant={'outline-primary'}
                                name="radio"
                                value={'sent'}
                                checked={currentState === 'sent'}
                                onChange={(e) => setCurrentState('sent')}
                            >
                                発送済
                            </ToggleButton>
                        </ButtonGroup>
                        <ButtonGroup>
                            <ToggleButton
                                id={`radio-pcr`}
                                disabled={api.loading}
                                type="radio"
                                variant={'outline-primary'}
                                name="inspectionMethod"
                                value={'pcr'}
                                checked={inspectionMethod === 'PCR'}
                                onChange={(e) => setInspectionMethod('PCR')}
                            >
                                PCR
                            </ToggleButton>
                            <ToggleButton
                                id={`radio-ngs`}
                                disabled={api.loading}
                                type="radio"
                                variant={'outline-primary'}
                                name="inspectionMethod"
                                value={'ngs'}
                                checked={inspectionMethod === 'NGS'}
                                onChange={(e) => setInspectionMethod('NGS')}
                            >
                                NGS
                            </ToggleButton>
                        </ButtonGroup>
                        {!samplesApi.loading && (
                            <Button
                                variant="success"
                                onClick={() => {
                                    console.log(samplesApi.response.results)
                                    const cancers = ['lung', 'stomach', 'esophagus', 'pancreas', 'colorectal', 'ovary', 'breast']
                                    const lines: string[] = [
                                        `,,結果表ID,URL,検品日,施設,検体コード,${cancers
                                            .map((cancer) => [cancer, '総合評価', 'パーソナルリスク', 'miRNAリスク', 'リスクスコア'])
                                            .flat()
                                            .join(',')}`,
                                    ]
                                    samplesApi.response.results.map((s) => {
                                        const results: (string | number)[][] = []
                                        cancers.forEach((cancer) => {
                                            const r = s.results?.find((r) => r.productItem?.nameEn === cancer)
                                            if (!r) {
                                                results.push(['', '', '', '', ''])
                                                return
                                            }
                                            const vm = new ResultV2ViewModel(r, cancer, '', '', s)
                                            const info = vm.info()
                                            results.push([cancer, info.score.level, info.score.personal_score, info.score.miRNA_score, info.score.ML_score])
                                        })
                                        lines.push(
                                            `,,${s.sendHistoryId},${
                                                process.env.REACT_APP_FRONT_URL + '/send_histories/preview_results?sampleId=' + s.id
                                            },${toDateText(s.inspectionDate)},${s.institution?.name},${s.code},${results.flat().join(',')}`
                                        )
                                    })
                                    downloadFile(lines.join('\n'), 'results.csv', 'text/csv', true)
                                }}
                            >
                                結果CSV出力
                            </Button>
                        )}
                        <Button
                            variant="link"
                            onClick={() => {
                                setIsConfirmMode(!isConfirmMode)
                            }}
                        >
                            {isConfirmMode ? '結果表印刷一覧に戻る' : `管理者確認待ちの重要結果表 ${confirmSampleApi.response.results.length}件`}
                        </Button>
                    </>
                }
                right={
                    <>
                        <Button
                            onClick={() => {
                                if (isConfirmMode) {
                                    sampleResultConfirmApi.execute(form)
                                    return
                                }

                                const isCheckTrackNumber = form.object?.every((s) => s.code === s.trackNumber || !s.trackNumber)

                                if (isCheckTrackNumber) {
                                    sampleResultStatusApi.execute(form)
                                } else {
                                    globalState.showConfirm(
                                        <div>
                                            検体コードと確認コードが一致していません。
                                            <br />
                                            検体コードを確認してください。
                                        </div>
                                    )
                                }
                            }}
                        >
                            保存
                        </Button>
                    </>
                }
            />
            <Flex style={{ marginTop: 5 }} alignItems="center" justifyContent="space-between">
                <Flex alignItems="center">
                    <InputField form={searchForm} attr="code" placeholder="コード検索" style={{ margin: 0 }} />
                    <InputField form={searchForm} attr="trackNumber" placeholder="追跡番号検索" style={{ margin: 0 }} />
                    <Button
                        style={{ width: 150 }}
                        variant="secondary"
                        onClick={() => {
                            searchForm.update((f) => {
                                delete f.code
                                delete f.trackNumber
                            })
                        }}
                    >
                        クリア
                    </Button>
                </Flex>
                <Flex alignItems="center">
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                    <div style={{ minWidth: 140, textAlign: 'right' }}>総件数： {api.response.count.toLocaleString()}件</div>
                </Flex>
            </Flex>
            <ContentBody>
                <TableWrapper showFooter={multipleCheckForm.form.object.items.length > 0} contentHeaderCount={2}>
                    <ExTable className="sticky-table">
                        <TableHead>
                            <CheckboxTableRow multipleCheckForm={multipleCheckForm} allItems={api.response.results} head allCheck>
                                <TableCell th align="center" width={45}>
                                    ID
                                </TableCell>
                                <TableCell th align="center" width={90}>
                                    印刷状況
                                </TableCell>
                                <TableCell th minWidth={300}>
                                    施設
                                </TableCell>
                                <TableCell th width={100}>
                                    コード
                                </TableCell>
                                <TableCell th width={100} align="center">
                                    ステータス
                                </TableCell>
                                <TableCell th width={100} align="center">
                                    再採尿
                                </TableCell>

                                {currentState === 'sent' ? (
                                    <OrderTableCell th width={100} form={searchForm} searchType={{ name: 'sendDate', type: 'date' }}>
                                        発送日
                                    </OrderTableCell>
                                ) : (
                                    <TableCell th width={100}>
                                        発送期限
                                    </TableCell>
                                )}

                                <TableCell th align="center" width={90}>
                                    最優先
                                </TableCell>
                                <TableCell th width={100}>
                                    結果表リンク
                                </TableCell>
                                {isConfirmMode ? (
                                    <TableCell th width={200}>
                                        管理者結果表内容確認
                                    </TableCell>
                                ) : (
                                    <>
                                        <TableCell th width={140}>
                                            結果表内容確認
                                        </TableCell>
                                        <TableCell th width={140}>
                                            結果表確認担当者
                                        </TableCell>
                                        <TableCell th width={120}>
                                            結果表印刷
                                        </TableCell>
                                        <TableCell th width={200}>
                                            確認コード
                                        </TableCell>
                                    </>
                                )}
                                <TableCell th width={300}>
                                    通知メッセージ
                                </TableCell>
                                <TableCell th width={300}>
                                    結果表返送時のメモ
                                </TableCell>
                            </CheckboxTableRow>
                        </TableHead>
                        <TableBody loading={api.loading} columnLength={15}>
                            {samples.map((sample: Sample, index: number) => {
                                const vm = new SampleViewModel(sample)
                                const isCompleteResultCheck = sample.isPrintResultChecked && sample.isResultChecked && !sample.trackNumber
                                return (
                                    <CheckboxTableRow key={`send-history-${sample.id}`} multipleCheckForm={multipleCheckForm} item={sample}>
                                        <TableCell align="center">{sample.id}</TableCell>
                                        <TableCell align="center">
                                            {sample.trackNumber ? <Badge bg="success">完了</Badge> : <Badge bg="secondary">処理中</Badge>}
                                        </TableCell>

                                        <TableCell>{sample.institution?.name}</TableCell>
                                        <TableCell>{sample.code}</TableCell>
                                        <TableCell align="center">{vm.getSampleResultStatusBadge()}</TableCell>
                                        <TableCell align="center">{sample.isRecollect ? <Badge bg="info">再採尿</Badge> : '-'}</TableCell>
                                        {currentState === 'sent' ? <TableCell>{vm.sendDateText()}</TableCell> : <TableCell>{vm.deadline()}</TableCell>}
                                        <TableCell align="center">
                                            {vm.object.priority === 1 ? (
                                                <Badge bg="success">特急</Badge>
                                            ) : vm.object.priority === 2 ? (
                                                <Badge bg="danger">超特急</Badge>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>

                                        <TableCell>
                                            <a
                                                href={`/send_histories/preview_results?sampleId=${sample.id}&showMedicalNote=true`}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                結果表リンク
                                            </a>
                                        </TableCell>

                                        {isConfirmMode ? (
                                            <TableCell
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                }}
                                            >
                                                {/* memo: isResultConfirmRequiredがfalseの場合、確認済になる */}
                                                <CheckboxField
                                                    form={form}
                                                    attr={[index, 'requestForm', 'isResultConfirmRequired']}
                                                    checkboxLabel="確認済"
                                                    value={!form.object[index].requestForm?.isResultConfirmRequired}
                                                    onChange={(e) => {
                                                        form.updateObject([index, 'requestForm', 'isResultConfirmRequired'], !e?.target.checked)
                                                    }}
                                                />
                                            </TableCell>
                                        ) : (
                                            <>
                                                <TableCell
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                    }}
                                                >
                                                    <CheckboxField
                                                        form={form}
                                                        attr={[index, 'isResultChecked']}
                                                        checkboxLabel="確認済"
                                                        disabled={sample.isResultChecked}
                                                    />
                                                </TableCell>
                                                <TableCell>{sample.resultCheckedUser?.fullName}</TableCell>
                                                <TableCell
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                    }}
                                                >
                                                    <CheckboxField
                                                        form={form}
                                                        attr={[index, 'isPrintResultChecked']}
                                                        disabled={sample.isPrintResultChecked}
                                                        checkboxLabel="印刷済み"
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                    }}
                                                >
                                                    <div style={{ position: 'relative' }}>
                                                        <InputField
                                                            form={form}
                                                            attr={[index, 'trackNumber']}
                                                            style={{ margin: 0 }}
                                                            disabled={!isCompleteResultCheck}
                                                        />
                                                        {sample.status === 'COMPLETED' &&
                                                            form.object[index] &&
                                                            form.object[index].trackNumber &&
                                                            sample.code !== form.object[index].trackNumber && (
                                                                <div style={{ fontSize: 10, color: 'red', position: 'absolute', bottom: -15, left: 4 }}>
                                                                    正しいコードをスキャンしてください
                                                                </div>
                                                            )}
                                                    </div>
                                                </TableCell>
                                            </>
                                        )}
                                        <TableCell>{form.object[index].requestForm?.noticeMessage}</TableCell>
                                        <TableCell>
                                            <InputField form={form} attr={[index, 'resultNote']} style={{ margin: 0 }} as="textarea" />
                                        </TableCell>
                                    </CheckboxTableRow>
                                )
                            })}
                        </TableBody>
                    </ExTable>
                    <Pagination pageSet={api.pageSet} response={api.response} searchForm={searchForm}></Pagination>
                </TableWrapper>
            </ContentBody>

            <FixedFooter open={multipleCheckForm.form.object.items.length > 0}>
                <Button
                    variant="secondary"
                    onClick={() => {
                        window.open('/send_histories/preview_request_post_list?showMedicalNote=true&sampleId=' + multipleCheckForm.ids().join(','), '_blank')
                    }}
                >
                    後納郵便差出票印刷
                </Button>
                <Button
                    variant="secondary"
                    onClick={() => {
                        window.open('/send_histories/preview_results?showMedicalNote=true&sampleId=' + multipleCheckForm.ids().join(','), '_blank')
                    }}
                >
                    結果表印刷
                </Button>

                <ModalButton
                    modalProps={{ fullscreen: true }}
                    openButton={<Button>結果一覧表示</Button>}
                    modalBody={(() => {
                        console.log(samplesApi.response.results)
                        const cancers = ['lung', 'stomach', 'esophagus', 'pancreas', 'colorectal', 'ovary', 'breast']
                        const lines: any[][] = []
                        const round = (value?: number) => {
                            if (!value) {
                                return ''
                            }
                            return String(Math.round(value * 100) / 100)
                        }
                        multipleCheckForm.form.object.items.map((s) => {
                            const results: (string | number)[][] = []
                            cancers.forEach((cancer) => {
                                const r = s.results?.find((r) => r.productItem?.nameEn === cancer)
                                if (!r) {
                                    results.push(['', '', '', '', ''])
                                    return
                                }
                                const vm = new ResultV2ViewModel(r, cancer, '', '', s)
                                const info = vm.info()
                                results.push([
                                    cancer,
                                    info.score.level,
                                    round(info.score.personal_score),
                                    round(info.score.miRNA_score),
                                    Math.round(info.score.ML_score * 1000) / 1000,
                                ])
                            })
                            lines.push([
                                ...[
                                    s.sendHistoryId,
                                    // process.env.REACT_APP_FRONT_URL + '/send_histories/preview_results?sampleId=' + s.id,
                                    toDateText(s.inspectionDate),
                                    s.institution?.name,
                                    s.code,
                                ],
                                ...results.flat(),
                            ])
                        })
                        const colProps = { style: { minWidth: 100 } }
                        return (
                            <Table bordered>
                                <thead>
                                    <tr>
                                        <th {...colProps}>結果表ID</th>
                                        <th {...colProps}>検品日</th>
                                        <th {...colProps} style={{ minWidth: 300 }}>
                                            施設
                                        </th>
                                        <th {...colProps}>検体コード</th>
                                        {cancers
                                            .map((cancer) => [
                                                <>
                                                    <th {...colProps}>{cancer}</th>
                                                    <th {...colProps}>総合評価</th>
                                                    <th {...colProps}>PR</th>
                                                    <th {...colProps}>MR</th>
                                                    <th {...colProps}>Score</th>
                                                </>,
                                            ])
                                            .flat()}
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.map((line, row) => {
                                        return (
                                            <tr key={`row-${row}`}>
                                                {line.map((value, col) => {
                                                    return <td key={`value-${row}-${col}`}>{value}</td>
                                                })}
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </Table>
                        )
                    })()}
                />
            </FixedFooter>
        </>
    )
}

export default withRouter(SendHistoriesPage)
